import {Matrix4,Vector3,Vector4} from 'three';

type Raster={width:number;height:number;data:ArrayLike<number>};
export type CloneSurface={patch:Vector4|null;patches:Vector4[];color:[number,number,number]};
type Frame={origin:Vector3;u:Vector3;v:Vector3;normal:Vector3;uLength:number;vLength:number};
const frame=(p:Vector3[]):Frame=>{const a=p[1].clone().sub(p[0]),b=p[3].clone().sub(p[0]);return {origin:p[0],u:a.clone().normalize(),v:b.clone().normalize(),normal:a.clone().cross(b).normalize(),uLength:a.length(),vLength:b.length()};};
const median=(values:number[])=>{values.sort((a,b)=>a-b);return values[Math.floor(values.length/2)]??220;};
/** Classify by the first room surface the original camera ray actually hits.
 * Merely falling inside the photograph does not make a pixel valid for every plane. */
function hit(ray:Vector3,frames:Frame[]){
 let nearest=Infinity,result:{surface:number;a:number;b:number}|null=null;
 for(let surface=0;surface<frames.length;surface++){
  const f=frames[surface],denominator=f.normal.dot(ray);if(Math.abs(denominator)<1e-8)continue;
  const t=f.normal.dot(f.origin)/denominator;if(t<=.01||t>=nearest)continue;
  const point=ray.clone().multiplyScalar(t).sub(f.origin),a=point.dot(f.u),b=point.dot(f.v);
  if(a<0||b<0||a>f.uLength||b>f.vLength)continue;
  nearest=t;result={surface,a,b};
 }
 return result;
}
export function classifyRoomRay(ray:Vector3,planes:Vector3[][]){return hit(ray,planes.map(frame))?.surface??-1;}

/** Scan existing room pixels. Donors remain on their own surface; contrast and
 * color outliers rank below representative paint / carpet / ceiling patches.
 * Nothing in the captured photograph is repainted by this analysis. */
export function analyzeRoomSurfaces(image:Raster,planes:Vector3[][],projection:Matrix4){
 const frames=planes.map(frame),inverse=projection.clone().invert(),{width,height,data}=image;
 const mask=new Uint8Array(width*height*4),ids=new Int8Array(width*height).fill(-1);
 const colors:number[][][]=planes.map(()=>[[],[],[]]),centers:{a:number;b:number}[][]=planes.map(()=>[]);
 const ray=new Vector3();
 for(let y=0;y<height;y++)for(let x=0;x<width;x++){
  ray.set((x+.5)/width*2-1,1-(y+.5)/height*2,.5).applyMatrix4(inverse);
  const found=hit(ray,frames);if(!found)continue;
  const id=found.surface;ids[y*width+x]=id;
  // DataTexture UV origin is bottom-left; canvas pixels are top-left.
  const m=((height-1-y)*width+x)*4;mask[m]=(id+1)*50;mask[m+3]=255;
  if(x%8===4&&y%8===4){for(let c=0;c<3;c++)colors[id][c].push(data[(y*width+x)*4+c]);}
  if(x%16===8&&y%16===8)centers[id].push({a:found.a,b:found.b});
 }
 const surfaces:CloneSurface[]=frames.map((f,id)=>{
  const rgb=colors[id].map(median),color=rgb.map(v=>v/255) as [number,number,number];
  let best:Vector4|null=null,bestScore=Infinity;
  const candidates:{patch:Vector4;score:number}[]=[];
  // Flooring needs several real boards, not the smallest quiet swatch of grain.
  // Keep wall donor selection unchanged.
  for(const center of centers[id])for(const size of (id===2?[.45,.75,1.2,1.8,2.4]:[.24,.45,.75])){
   const startA=center.a-size/2,startB=center.b-size/2;
   if(startA<.04||startB<.04||startA+size>f.uLength-.04||startB+size>f.vLength-.04)continue;
   const samples:number[][]=[];let valid=true,edgePenalty=0;
   for(let row=0;row<7&&valid;row++)for(let col=0;col<7;col++){
    const world=f.origin.clone().addScaledVector(f.u,startA+size*col/6).addScaledVector(f.v,startB+size*row/6);
    if(world.z>=-.05){valid=false;break;}
    const p=world.applyMatrix4(projection),x=Math.floor((p.x*.5+.5)*width),y=Math.floor((.5-p.y*.5)*height);
    if(x<2||y<2||x>=width-2||y>=height-2||ids[y*width+x]!==id){valid=false;break;}
    const at=(y*width+x)*4,rgbHere=[data[at],data[at+1],data[at+2]];samples.push(rgbHere);
    edgePenalty+=rgbHere.reduce((sum,value,c)=>sum+Math.abs(value-data[at+4+c])+Math.abs(value-data[at+width*4+c]),0)/6;
   }
   if(!valid||samples.length!==49)continue;
   const avg=[0,1,2].map(c=>samples.reduce((sum,p)=>sum+p[c],0)/49);
   const mismatch=avg.reduce((sum,v,c)=>sum+Math.abs(v-rgb[c]),0)/3;
   const variance=samples.reduce((sum,p)=>sum+p.reduce((s,v,c)=>s+Math.abs(v-avg[c]),0)/3,0)/49;
   // Larger stable patches reduce visible repetition; hard edges and unusual
   // colors avoid copying windows, outlets and leftover object boundaries.
   const score=id===2?mismatch+variance*.15+edgePenalty/49*.2-size*14:mismatch+variance*.6+edgePenalty/49*.8-size*3;
   const patch=new Vector4(startA,startB,size,size);candidates.push({patch,score});
   if(score<bestScore){bestScore=score;best=patch;}
  }
  candidates.sort((a,b)=>a.score-b.score);
  const patches:Vector4[]=[];
  for(const candidate of candidates){
   if(candidate.score>bestScore+12)break;
   const p=candidate.patch;
   if(patches.some(q=>Math.hypot(p.x+p.z/2-q.x-q.z/2,p.y+p.w/2-q.y-q.w/2)<Math.max(p.z,q.z)))continue;
   patches.push(p);if(patches.length===4)break;
  }
  return {patch:best,patches,color};
 });
 return {mask,surfaces};
}


/** Exclude mixed edge texels from photo projection. The exact mesh boundary
 * remains the seam; each side falls back to its own surface texture. */
export function isolateRoomSurfaceEdges(mask:Uint8Array,width:number,height:number){
 const isolated=mask.slice();
 for(let y=0;y<height;y++)for(let x=0;x<width;x++){
  const at=(y*width+x)*4,id=mask[at];if(!id)continue;
  for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++){
   const nx=x+dx,ny=y+dy;
   if(nx>=0&&ny>=0&&nx<width&&ny<height&&mask[(ny*width+nx)*4]!==id)isolated[at]=0;
  }
 }
 return isolated;
}

